import {
  ChangelogContentBody,
  ChangelogContentWrapper,
  ChangelogHeaderBody,
  ChangelogHeaderWrapper,
  ChangelogWrapper,
} from 'components/changelog-page/ChangelogAssets'
import ChangelogErrorState from 'components/changelog-page/ChangelogErrorState'
import Svg from 'components/UI/SVG'
import SubHeaderLoadingSmall from 'images/changelog-subheader-loading-small.svg'
import RightArrow from 'images/right-arrow.svg'
import { ReactElement } from 'react'
import { PlatformInfo } from 'redux/api/wsApi'
import { Flex, get, Text } from 'theme-ui'

export default function DesktopPlatformItem({
  title,
  icon,
  isError,
  isLoading,
  platform,
  link,
  dataTestId,
}: {
  title: string
  icon: ReactElement
  isError: boolean
  isLoading: boolean
  platform: PlatformInfo | undefined
  link: string
  dataTestId?: string
}): ReactElement {
  if (!platform && !isLoading) {
    return <></>
  }
  if (isLoading) {
    return (
      <ChangelogWrapper
        link={link}
        linkStyles={{
          '&:only-child': {
            minWidth: ['100%', '46.6rem'],
          },
        }}
      >
        <ChangelogHeaderWrapper>
          <Svg svg={icon} sx={{ position: 'absolute', right: '1.6rem', bottom: 0 }} />
          <Flex sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Text variant="h3BigBold" color="white">
              {title}
            </Text>
          </Flex>
          <Flex>
            <Svg svg={SubHeaderLoadingSmall} />
          </Flex>
        </ChangelogHeaderWrapper>
        <ChangelogContentWrapper>
          <Flex sx={{ gap: '1.6rem', flexDirection: ['column', 'column', 'row'] }}>
            <Svg svg={SubHeaderLoadingSmall} />
            <Svg svg={SubHeaderLoadingSmall} />
          </Flex>
        </ChangelogContentWrapper>
      </ChangelogWrapper>
    )
  }

  return (
    <ChangelogWrapper link={link} dataTestId={dataTestId}>
      <ChangelogHeaderWrapper>
        <Svg svg={icon} sx={{ position: 'absolute', right: '1.6rem', bottom: 0 }} />
        <Flex sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Text variant="h3BigBold" color="white">
            {title}
          </Text>
          <Flex>
            <Svg
              svg={RightArrow}
              className="arrow"
              sx={{
                path: {
                  fill: 'white50',
                  transition: theme => `${get(theme, 'transitions.default')}`,
                },
              }}
            />
          </Flex>
        </Flex>
        {isError ? (
          <ChangelogErrorState />
        ) : (
          <ChangelogHeaderBody
            version={platform?.release_full_version}
            date={platform?.release_date}
          />
        )}
      </ChangelogHeaderWrapper>
      <ChangelogContentWrapper>
        {isError ? (
          <ChangelogErrorState />
        ) : (
          <ChangelogContentBody
            betaVersion={platform?.beta_full_version}
            guineaVersion={platform?.guinea_pig_full_version}
          />
        )}
      </ChangelogContentWrapper>
    </ChangelogWrapper>
  )
}

export function MobilePlatformItem({
  title,
  icon,
  isError,
  isLoading,
  platform,
  link,
  dataTestId,
}: {
  title: string
  icon: React.ReactElement
  isError: boolean
  isLoading: boolean
  platform: PlatformInfo | undefined
  link: string
  dataTestId?: string
}): ReactElement {
  if (!platform && !isLoading && !isError) {
    return <></>
  }

  if (isLoading) {
    return (
      <ChangelogWrapper link={link}>
        <ChangelogHeaderWrapper>
          <Flex sx={{ justifyContent: 'space-between' }}>
            <Text variant="heading2" color="white" data-testid={dataTestId}>
              {title}
            </Text>
          </Flex>
          <Svg svg={SubHeaderLoadingSmall} />
        </ChangelogHeaderWrapper>
      </ChangelogWrapper>
    )
  }

  return (
    <ChangelogWrapper link={link}>
      <ChangelogHeaderWrapper>
        <Svg svg={icon} sx={{ position: 'absolute', right: '1.6rem', bottom: 0 }} />
        <Flex sx={{ justifyContent: 'space-between' }}>
          <Text variant="heading2" color="white">
            {title}
          </Text>
          <Flex>
            <Svg
              svg={RightArrow}
              className="arrow"
              sx={{
                path: {
                  fill: 'white50',
                  transition: theme => `${get(theme, 'transitions.default')}`,
                },
              }}
            />
          </Flex>
        </Flex>
        {isError ? (
          <ChangelogErrorState />
        ) : (
          <ChangelogHeaderBody
            version={platform?.release_full_version}
            date={platform?.release_date}
          />
        )}
      </ChangelogHeaderWrapper>
    </ChangelogWrapper>
  )
}
